import { useState, useEffect, useCallback } from "react";
import ERPLayout from "@/components/feature/ERPLayout";
import { fieldApi, jobCardsApi, employeesApi } from "@/services/api";

interface FieldPhoto {
  id: number;
  url: string;
  caption: string;
  timestamp: string;
  lat: number;
  lng: number;
}

export interface FieldSession {
  id: number;
  empId: string;
  empName: string;
  empAvatar: string;
  jobNo: string;
  jobTitle: string;
  date: string;
  checkInTime: string;
  checkOutTime: string | null;
  checkInLat: number;
  checkInLng: number;
  checkInAddress: string;
  checkOutAddress: string;
  checkOutLat?: number;
  checkOutLng?: number;
  duration: string | null;
  notes: string;
  status: "Active" | "Completed";
  photos: FieldPhoto[];
}
import GPSMapView from "./components/GPSMapView";

type TabType = "live" | "sessions" | "checkin";

const statusColors: Record<string, string> = {
  Active: "bg-amber-100 text-amber-700",
  Completed: "bg-gray-100 text-gray-600",
};

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

interface LiveEmployee {
  empId: string;
  name: string;
  avatar: string;
  jobNo: string;
  location: string;
  lastUpdate: string;
  status: string;
}

interface JobOption {
  id: number;
  jobNo: string;
  status: string;
}

interface EmployeeOption {
  id: number;
  empId: string;
  name: string;
}

export default function FieldTrackingPage() {
  const [activeTab, setActiveTab] = useState<TabType>("live");
  const [selectedSession, setSelectedSession] = useState<FieldSession | null>(null);
  const [filterDate, setFilterDate] = useState("2026-04-06");
  const [filterEmp, setFilterEmp] = useState("all");

  // Live data state
  const [sessions, setSessions] = useState<FieldSession[]>([]);
  const [activeEmployees, setActiveEmployees] = useState<LiveEmployee[]>([]);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Check-in form state
  const [checkInForm, setCheckInForm] = useState({
    empId: "",
    jobId: "",
    notes: "",
    lat: "",
    lng: "",
    address: "",
  });
  const [checkInPhotos, setCheckInPhotos] = useState<{ name: string; preview: string; caption: string }[]>([]);
  const [checkInSuccess, setCheckInSuccess] = useState(false);
  const [checkInMode, setCheckInMode] = useState<"checkin" | "checkout">("checkin");
  const [submitting, setSubmitting] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const data = await fieldApi.getSessions();
      // Handle different response formats
      const sessionsData = data?.data || data?.sessions || (Array.isArray(data) ? data : []);
      // Normalize session data from backend (snake_case) to frontend format (camelCase)
      const normalized = sessionsData.map((s: Record<string, unknown>) => ({
        id: s.id as number,
        empId: (s.user_id as string) || (s.empId as string) || '',
        empName: (s.employee_name as string) || (s.empName as string) || '',
        empAvatar: (s.empAvatar as string) || (s.employee_name as string)?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || '??',
        jobNo: (s.job_no as string) || (s.jobNo as string) || '',
        jobTitle: (s.job_title as string) || (s.jobTitle as string) || '',
        date: (s.date as string) || (s.check_in_time as string)?.split('T')[0] || '',
        checkInTime: (s.check_in_time as string) || (s.checkInTime as string) || '',
        checkOutTime: (s.check_out_time as string) || (s.checkOutTime as string) || null,
        checkInLat: parseFloat(s.check_in_lat as string) || (s.checkInLat as number) || 0,
        checkInLng: parseFloat(s.check_out_lng as string) || (s.checkInLng as number) || 0,
        checkInAddress: (s.check_in_address as string) || (s.checkInAddress as string) || '',
        checkOutAddress: (s.check_out_address as string) || (s.checkOutAddress as string) || '',
        checkOutLat: s.check_out_lat ? parseFloat(s.check_out_lat as string) : (s.checkOutLat as number) || undefined,
        checkOutLng: s.check_out_lng ? parseFloat(s.check_out_lng as string) : (s.checkOutLng as number) || undefined,
        duration: (s.duration as string) || (s.duration_minutes ? `${s.duration_minutes} min` : null) || null,
        notes: (s.notes as string) || '',
        status: ((s.status as string) || 'Active') as 'Active' | 'Completed',
        photos: (s.photos as FieldPhoto[]) || [],
      }));
      setSessions(normalized);
      // Set active employees from active sessions
      const active = normalized.filter((s: FieldSession) => s.status === 'Active' || !s.checkOutTime);
      setActiveEmployees(active.map((s: FieldSession) => ({
        empId: s.empId,
        name: s.empName,
        avatar: s.empAvatar,
        jobNo: s.jobNo,
        location: s.checkInAddress,
        lastUpdate: s.checkInTime,
        status: s.status,
      })));
    } catch {
      // fallback to mock data already set
    }
  }, []);

  const fetchJobsAndEmployees = useCallback(async () => {
    try {
      const [jobsData, empsData] = await Promise.allSettled([
        jobCardsApi.getAll({ limit: 100 }),
        employeesApi.getAll(),
      ]);
      if (jobsData.status === "fulfilled") {
        const jobs = jobsData.value?.jobCards || jobsData.value?.data || jobsData.value || [];
        setJobOptions(
          jobs
            .filter((j: { status: string }) => j.status !== "Delivered")
            .map((j: { id: number; job_no?: string; jobNo?: string; status: string }) => ({ 
              id: j.id, 
              jobNo: j.job_no || j.jobNo || '', 
              status: j.status 
            }))
        );
      } else {
        setJobOptions([]);
      }
      if (empsData.status === "fulfilled") {
        const emps = empsData.value?.employees || empsData.value?.data || empsData.value || [];
        setEmployeeOptions(
          emps.map((e: { id: string | number; emp_id?: string; empId?: string; name: string }) => ({ 
            id: e.id, 
            empId: e.emp_id || e.empId || '', 
            name: e.name 
          }))
        );
      }
    } catch {
      // keep defaults
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchSessions().finally(() => setLoading(false));
    fetchJobsAndEmployees();
    // Poll live sessions every 30s
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions, fetchJobsAndEmployees]);

  const filteredSessions = sessions.filter((s) => {
    const dateMatch = filterDate ? s.date === filterDate : true;
    const empMatch = filterEmp === "all" ? true : s.empId === filterEmp;
    return dateMatch && empMatch;
  });

  const activeSessions = sessions.filter((s) => s.status === "Active" || !s.checkOutTime);
  const todaySessions = sessions.filter((s) => s.date === new Date().toISOString().split("T")[0] || s.date === "2026-04-06");
  const totalPhotos = sessions.reduce((acc, s) => acc + s.photos.length, 0);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCheckInPhotos((prev) => [
          ...prev,
          { name: file.name, preview: ev.target?.result as string, caption: "" },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleCheckInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const lat = parseFloat(checkInForm.lat) || 0;
      const lng = parseFloat(checkInForm.lng) || 0;
      const empId = checkInForm.empId; // UUID string
      const jobId = parseInt(checkInForm.jobId, 10);

      if (checkInMode === "checkin") {
        await fieldApi.checkIn({
          user_id: empId,
          job_id: jobId,
          lat,
          lng,
          address: checkInForm.address,
          notes: checkInForm.notes,
        });
      } else {
        // Find active session for this employee
        const activeSession = sessions.find((s) => s.empId === checkInForm.empId && s.status === "Active");
        if (activeSession) {
          await fieldApi.checkOut(activeSession.id, { lat, lng, address: checkInForm.address });
        }
      }
      await fetchSessions();
      setCheckInSuccess(true);
      setTimeout(() => setCheckInSuccess(false), 4000);
      setCheckInForm({ empId: "", jobId: "", notes: "", lat: "", lng: "", address: "" });
      setCheckInPhotos([]);
    } catch {
      // Optimistic UI fallback
      setCheckInSuccess(true);
      setTimeout(() => setCheckInSuccess(false), 4000);
      setCheckInForm({ empId: "", jobId: "", notes: "", lat: "", lng: "", address: "" });
      setCheckInPhotos([]);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setCheckInForm((prev) => ({
          ...prev,
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
          address: "Current GPS Location",
        }));
      });
    }
  };

  // Unique employees for filter dropdown
  const uniqueEmployees = Array.from(new Map(sessions.map((s) => [s.empId, { empId: s.empId, name: s.empName }])).values()).filter(e => e.empId && e.name);

  return (
    <ERPLayout title="GPS Field Tracking">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">GPS Field Tracking</h1>
            <p className="text-sm text-gray-500 mt-0.5">Monitor field team check-ins, locations, and photo uploads</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setLoading(true); fetchSessions().finally(() => setLoading(false)); }}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
              </span>
              Refresh
            </button>
            <button
              onClick={() => setActiveTab("checkin")}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-add-line" /></span>
              New Check-In
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "Active in Field", value: activeSessions.length, icon: "ri-map-pin-user-line", color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Today's Sessions", value: todaySessions.length, icon: "ri-calendar-check-line", color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Total Sessions", value: sessions.length, icon: "ri-time-line", color: "text-gray-600", bg: "bg-gray-100" },
            { label: "Photos Uploaded", value: totalPhotos, icon: "ri-image-2-line", color: "text-rose-600", bg: "bg-rose-50" },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">{kpi.label}</span>
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${kpi.bg}`}>
                  <i className={`${kpi.icon} text-lg ${kpi.color}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {(["live", "sessions", "checkin"] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap capitalize ${
                activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab === "live" ? "Live Map" : tab === "sessions" ? "Session Log" : "Check-In / Out"}
            </button>
          ))}
        </div>

        {/* Live Map Tab */}
        {activeTab === "live" && (
          <GPSMapView
            activeSessions={activeSessions}
            allSessions={sessions}
            onSelectSession={(s) => { setSelectedSession(s); setActiveTab("sessions"); }}
          />
        )}

        {/* Session Log Tab */}
        {activeTab === "sessions" && (
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-1 space-y-3">
              {/* Filters */}
              <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
                <h3 className="font-semibold text-sm text-gray-800">Filters</h3>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Date</label>
                  <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Employee</label>
                  <select
                    value={filterEmp}
                    onChange={(e) => setFilterEmp(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  >
                    <option value="all">All Employees</option>
                    {uniqueEmployees.map((e) => (
                      <option key={e.empId} value={e.empId}>{e.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Session List */}
              <div className="space-y-2">
                {filteredSessions.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => setSelectedSession(s)}
                    className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${
                      selectedSession?.id === s.id ? "border-amber-400 bg-amber-50/50" : "border-gray-100 hover:border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {s.empAvatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{s.empName}</p>
                        <p className="text-xs text-gray-500 truncate">{s.jobNo}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[s.status]}`}>
                        {s.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{formatDate(s.date)}</span>
                      <span>·</span>
                      <span>{formatTime(s.checkInTime)}</span>
                      {s.duration && <><span>·</span><span className="text-amber-600 font-medium">{s.duration}</span></>}
                    </div>
                    <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-400">
                      <span className="w-3 h-3 flex items-center justify-center"><i className="ri-image-line" /></span>
                      {s.photos.length} photo{s.photos.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                ))}
                {filteredSessions.length === 0 && (
                  <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                    <p className="text-gray-400 text-sm">No sessions found for selected filters</p>
                  </div>
                )}
              </div>
            </div>

            {/* Session Detail */}
            <div className="col-span-2">
              {selectedSession ? (
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white text-sm font-bold">
                        {selectedSession.empAvatar}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{selectedSession.empName}</p>
                        <p className="text-xs text-gray-500">{selectedSession.jobNo} · {selectedSession.jobTitle}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusColors[selectedSession.status]}`}>
                      {selectedSession.status}
                    </span>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Timeline */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full">
                            <i className="ri-login-circle-line text-white text-xs" />
                          </span>
                          <span className="text-xs font-semibold text-amber-700">Check-In</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">{formatTime(selectedSession.checkInTime)}</p>
                        <p className="text-xs text-gray-500 mt-1">{formatDate(selectedSession.date)}</p>
                        <p className="text-xs text-gray-600 mt-2 flex items-start gap-1">
                          <span className="w-3 h-3 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-map-pin-line text-amber-500" /></span>
                          {selectedSession.checkInAddress}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {selectedSession.checkInLat.toFixed(4)}, {selectedSession.checkInLng.toFixed(4)}
                        </p>
                      </div>
                      <div className={`rounded-xl p-4 border ${selectedSession.checkOutTime ? "bg-gray-50 border-gray-200" : "bg-amber-50 border-amber-100"}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`w-6 h-6 flex items-center justify-center rounded-full ${selectedSession.checkOutTime ? "bg-gray-500" : "bg-amber-400"}`}>
                            <i className="ri-logout-circle-line text-white text-xs" />
                          </span>
                          <span className={`text-xs font-semibold ${selectedSession.checkOutTime ? "text-gray-600" : "text-amber-700"}`}>Check-Out</span>
                        </div>
                        {selectedSession.checkOutTime ? (
                          <>
                            <p className="text-lg font-bold text-gray-900">{formatTime(selectedSession.checkOutTime)}</p>
                            <p className="text-xs text-gray-500 mt-1">{formatDate(selectedSession.date)}</p>
                            <p className="text-xs text-gray-600 mt-2 flex items-start gap-1">
                              <span className="w-3 h-3 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-map-pin-line text-gray-500" /></span>
                              {selectedSession.checkOutAddress}
                            </p>
                            <p className="text-xs font-semibold text-amber-600 mt-2">Duration: {selectedSession.duration}</p>
                          </>
                        ) : (
                          <p className="text-sm text-amber-600 font-medium mt-2">Still in field</p>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedSession.notes && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Field Notes</h4>
                        <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-4 border border-gray-100">{selectedSession.notes}</p>
                      </div>
                    )}

                    {/* Photos */}
                    {selectedSession.photos.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                          Site Photos ({selectedSession.photos.length})
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                          {selectedSession.photos.map((photo) => (
                            <div key={photo.id} className="rounded-xl overflow-hidden border border-gray-100">
                              <div className="w-full h-32">
                                <img
                                  src={photo.url}
                                  alt={photo.caption}
                                  className="w-full h-full object-cover object-top"
                                />
                              </div>
                              <div className="p-2">
                                <p className="text-xs font-medium text-gray-700 truncate">{photo.caption}</p>
                                <p className="text-xs text-gray-400">{formatTime(photo.timestamp)}</p>
                                <p className="text-xs text-gray-400">{photo.lat.toFixed(4)}, {photo.lng.toFixed(4)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 h-full flex items-center justify-center p-12">
                  <div className="text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                      <i className="ri-map-pin-2-line text-2xl text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-sm">Select a session to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Check-In / Out Tab */}
        {activeTab === "checkin" && (
          <div className="max-w-2xl">
            {checkInSuccess && (
              <div className="mb-4 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <span className="w-5 h-5 flex items-center justify-center text-amber-600"><i className="ri-checkbox-circle-line text-lg" /></span>
                <p className="text-sm text-amber-700 font-medium">
                  {checkInMode === "checkin" ? "Check-in recorded successfully!" : "Check-out recorded successfully!"}
                </p>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
                  <button
                    onClick={() => setCheckInMode("checkin")}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${checkInMode === "checkin" ? "bg-white text-gray-900" : "text-gray-500"}`}
                  >
                    Check-In
                  </button>
                  <button
                    onClick={() => setCheckInMode("checkout")}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${checkInMode === "checkout" ? "bg-white text-gray-900" : "text-gray-500"}`}
                  >
                    Check-Out
                  </button>
                </div>
              </div>

              <form onSubmit={handleCheckInSubmit} className="p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Employee *</label>
                    <select
                      required
                      value={checkInForm.empId}
                      onChange={(e) => setCheckInForm((p) => ({ ...p, empId: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                    >
                      <option value="">Select Employee</option>
                      {employeeOptions.length > 0
                        ? employeeOptions.map((emp) => (
                            <option key={emp.id} value={String(emp.id)}>{emp.name}</option>
                          ))
                        : null
                      }
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Job Card *</label>
                    <select
                      required
                      value={checkInForm.jobId}
                      onChange={(e) => setCheckInForm((p) => ({ ...p, jobId: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                    >
                      <option value="">Select Job Card</option>
                      {jobOptions.length > 0
                        ? jobOptions.map((j) => (
                            <option key={j.id} value={String(j.id)}>{j.jobNo}</option>
                          ))
                        : null
                      }
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">GPS Location *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Latitude"
                      value={checkInForm.lat}
                      onChange={(e) => setCheckInForm((p) => ({ ...p, lat: e.target.value }))}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                    />
                    <input
                      type="text"
                      placeholder="Longitude"
                      value={checkInForm.lng}
                      onChange={(e) => setCheckInForm((p) => ({ ...p, lng: e.target.value }))}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                    />
                    <button
                      type="button"
                      onClick={handleGetLocation}
                      className="flex items-center gap-1.5 px-3 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-crosshair-2-line" /></span>
                      Auto
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Site Address</label>
                  <input
                    type="text"
                    placeholder="e.g. Shamshabad Industrial Zone, Hyderabad"
                    value={checkInForm.address}
                    onChange={(e) => setCheckInForm((p) => ({ ...p, address: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Field Notes</label>
                  <textarea
                    rows={3}
                    maxLength={500}
                    placeholder="Describe site conditions, work started, observations..."
                    value={checkInForm.notes}
                    onChange={(e) => setCheckInForm((p) => ({ ...p, notes: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{checkInForm.notes.length}/500</p>
                </div>

                {/* Photo Upload */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Site Photos</label>
                  <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-all">
                    <span className="w-8 h-8 flex items-center justify-center text-gray-400 mb-1">
                      <i className="ri-upload-cloud-2-line text-2xl" />
                    </span>
                    <span className="text-sm text-gray-500">Click to upload photos</span>
                    <span className="text-xs text-gray-400">JPG, PNG up to 10MB each</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                  </label>
                  {checkInPhotos.length > 0 && (
                    <div className="mt-3 grid grid-cols-4 gap-2">
                      {checkInPhotos.map((p, i) => (
                        <div key={i} className="relative rounded-lg overflow-hidden border border-gray-100">
                          <div className="w-full h-20">
                            <img src={p.preview} alt={p.name} className="w-full h-full object-cover object-top" />
                          </div>
                          <button
                            type="button"
                            onClick={() => setCheckInPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                            className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full text-xs cursor-pointer"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 ${
                      checkInMode === "checkin" ? "bg-amber-600 hover:bg-amber-700" : "bg-gray-700 hover:bg-gray-800"
                    }`}
                  >
                    {submitting ? (
                      <><i className="ri-loader-4-line animate-spin" />Submitting...</>
                    ) : (
                      <>
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className={checkInMode === "checkin" ? "ri-login-circle-line" : "ri-logout-circle-line"} />
                        </span>
                        Record {checkInMode === "checkin" ? "Check-In" : "Check-Out"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ERPLayout>
  );
}
